import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { postService, commentService, acceptedAnswerService, uploadService, bookmarkService, subscriptionService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import { formatDate, getPostPrimaryBadgeLabel, getPostPrimaryBadgeColor } from '../utils/helpers';
import LevelBadge from '../components/LevelBadge';
import BlockRenderer from '../components/BlockRenderer';
import PollWidget from '../components/PollWidget';
import FollowButton from '../components/FollowButton';
import RelatedPosts from '../components/RelatedPosts';
import './PostDetailPage.css';

// 業務邏輯：將文字中的 @account 提及標示為高亮樣式，讓被點名者在留言中清楚可辨。
const renderWithMentions = (text) => String(text).split(/(@[A-Za-z0-9_]+)/g).map((part, i) => (
  /^@[A-Za-z0-9_]+$/.test(part) ? <span key={i} className="mention">{part}</span> : part
));

// Recursive component for nested comments.
// 業務邏輯：每一則留言只管理自己的直接子回覆，當直接子回覆超過 3 則時預設只顯示前 3 則，避免單一留言串過長影響閱讀。
const CommentThread = ({
  comment, post, user, depth = 0,
  onVote, onAcceptAnswer, onDelete,
  replyingTo, setReplyingTo, replyContent, onReplyContentChange, onSubmitReply, submitting,
}) => {
  const isReplying = replyingTo === comment.id;
  const [showAllReplies, setShowAllReplies] = useState(false);
  const replies = comment.replies || [];
  const shouldFoldReplies = replies.length > 3;
  const visibleReplies = shouldFoldReplies && !showAllReplies ? replies.slice(0, 3) : replies;
  const hiddenReplyCount = replies.length - visibleReplies.length;

  return (
    <div className={`comment-thread ${depth > 0 ? 'reply' : ''}`}>
      <div className={`comment ${post.accepted_comment_id === comment.id ? 'accepted' : ''}`}>
        {post.accepted_comment_id === comment.id && <div className="accepted-badge">✓ 最佳解答</div>}
        <div className="comment-header">
          <span className="author">{comment.author_name}<LevelBadge userId={comment.author_id} compact /></span>
          <span className="time">{formatDate(comment.created_at)}</span>
        </div>
        <div className="comment-content">
          {comment.content.split('\n').map((line, i) => <p key={i}>{renderWithMentions(line)}</p>)}
        </div>
        <div className="comment-actions">
          <button className={`btn-vote ${comment.user_has_voted ? 'voted' : ''}`} onClick={() => onVote(comment.id, comment.user_has_voted)}>
            👍 {comment.like_count || 0}
          </button>
          {user?.id === post.author_id && (
            <button className="btn-accept" onClick={() => onAcceptAnswer(comment.id)}>標記為最佳解答</button>
          )}
          {user && (
            <button className="btn-reply" onClick={() => setReplyingTo(isReplying ? null : comment.id)}>回覆</button>
          )}
          {user && (user.id === comment.author_id || user.role === 'ADMIN') && (
            <button className="btn-delete" onClick={() => { if (window.confirm('確定要刪除此留言嗎？')) onDelete(comment.id); }}>刪除</button>
          )}
        </div>

        {isReplying && (
          <form onSubmit={(e) => onSubmitReply(e, comment.id)} className="reply-form">
            <textarea className="form-control" placeholder="回覆此留言..." value={replyContent}
              onChange={(e) => onReplyContentChange(e.target.value)} rows="3" disabled={submitting} />
            <div className="reply-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting || !replyContent.trim()}>
                {submitting ? '提交中...' : '提交'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setReplyingTo(null)} disabled={submitting}>取消</button>
            </div>
          </form>
        )}
      </div>

      {replies.length > 0 && (
        <div className="replies">
          {visibleReplies.map((reply) => (
            <CommentThread key={reply.id} comment={reply} post={post} user={user} depth={depth + 1}
              onVote={onVote} onAcceptAnswer={onAcceptAnswer} onDelete={onDelete}
              replyingTo={replyingTo} setReplyingTo={setReplyingTo} replyContent={replyContent}
              onReplyContentChange={onReplyContentChange} onSubmitReply={onSubmitReply} submitting={submitting} />
          ))}
          {shouldFoldReplies && (
            <button
              type="button"
              className="btn-more-replies"
              onClick={() => setShowAllReplies(prev => !prev)}
            >
              {showAllReplies ? '收合回覆' : `展開更多回覆（剩餘 ${hiddenReplyCount} 則）`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PostDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 我訂閱的標籤集合，用於標籤旁追蹤按鈕的初始狀態
  const [subscribedTags, setSubscribedTags] = useState(new Set());
  // 代碼主題偏好：從用戶設定讀取，或使用暗色作為默認
  const [codeTheme, setCodeTheme] = useState(user?.code_theme_preference || 'dark');

  // 使用者資料為非同步載入：直接以網址進入本頁時 user 可能尚未就緒，載入完成後同步其代碼主題偏好
  useEffect(() => {
    if (user?.code_theme_preference) setCodeTheme(user.code_theme_preference);
  }, [user?.code_theme_preference]);

  useEffect(() => {
    if (!user) return;
    subscriptionService.list()
      .then((res) => {
        const tagIds = (res.data || []).filter((s) => s.target_type === 'TAG').map((s) => s.target_id);
        setSubscribedTags(new Set(tagIds));
      })
      .catch(() => { /* 靜默失敗 */ });
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [postRes, commentsRes, attachRes] = await Promise.all([
          postService.get(id),
          commentService.list(id),
          uploadService.listByPost(id),
        ]);
        setPost(postRes.data);
        setComments(commentsRes.data);
        setAttachments((attachRes.data || []).map(a => ({ ...a, url: uploadService.getFileUrl(`/api/uploads/file/${a.file_path}`) })));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await commentService.create(id, { content: newComment });
      setNewComment('');
      // Refresh comments
      const commentsRes = await commentService.list(id);
      setComments(commentsRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddReply = async (e, parentCommentId) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setSubmitting(true);
    try {
      await commentService.create(id, {
        content: replyContent,
        parentCommentId: parentCommentId
      });
      setReplyContent('');
      setReplyingTo(null);
      // Refresh comments
      const commentsRes = await commentService.list(id);
      setComments(commentsRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (commentId, hasVoted) => {
    try {
      if (hasVoted) {
        await commentService.unvote(commentId);
      } else {
        await commentService.vote(commentId);
      }
      const commentsRes = await commentService.list(id);
      setComments(commentsRes.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAcceptAnswer = async (commentId) => {
    try {
      await acceptedAnswerService.set(id, commentId);
      const postRes = await postService.get(id);
      setPost(postRes.data);
      const commentsRes = await commentService.list(id);
      setComments(commentsRes.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentService.delete(commentId);
      // Refresh comments
      const commentsRes = await commentService.list(id);
      setComments(commentsRes.data);
    } catch (err) {
      setError(err.message);
    }
  };

  // 文章投票：以樂觀更新即時反映讚數與按讚狀態
  const handleVotePost = async () => {
    if (!user) return;
    try {
      if (post.user_has_voted) {
        await postService.unvote(post.id);
        setPost((p) => ({ ...p, user_has_voted: 0, vote_count: Math.max((p.vote_count || 0) - 1, 0) }));
      } else {
        await postService.vote(post.id);
        setPost((p) => ({ ...p, user_has_voted: 1, vote_count: (p.vote_count || 0) + 1 }));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // 文章收藏切換
  const handleToggleBookmarkPost = async () => {
    if (!user) return;
    try {
      if (post.is_bookmarked) {
        await bookmarkService.remove(post.id);
        setPost((p) => ({ ...p, is_bookmarked: 0 }));
      } else {
        await bookmarkService.add(post.id);
        setPost((p) => ({ ...p, is_bookmarked: 1 }));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdatePostStatus = async (nextStatus) => {
    try {
      setError('');
      // 業務邏輯：只有討論型文章才提供作者結案，經驗分享不進入問題解決流程。
      await postService.updateStatus(id, nextStatus);
      const postRes = await postService.get(id);
      setPost(postRes.data);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="container mt-5">載入中...</div>;
  if (!post) return <div className="container mt-5">文章不存在</div>;
  const isDiscussionPost = (post.post_type || 'DISCUSSION') === 'DISCUSSION';
  const canResolvePost = user?.id === post.author_id && isDiscussionPost && post.status === 'OPEN';

  return (
    <div className="post-detail-page">
      <div className="container">
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="post-detail">
          <div className="post-header">
            <h1>{post.title}</h1>
            <div className="post-meta">
              <span className="author">作者: {post.author_name}<LevelBadge userId={post.author_id} compact /></span>
              <span className="time">{formatDate(post.created_at)}</span>
              <span className={`badge badge-${getPostPrimaryBadgeColor(post)}`}>
                {getPostPrimaryBadgeLabel(post)}
              </span>
              <span className="post-meta-stat">👁 {post.view_count || 0}</span>
              <span className="post-meta-stat">💬 {comments.length}</span>
            </div>
          </div>

          <div className="post-content">
            <BlockRenderer
              content={post.content}
              attachments={attachments}
              codeTheme={codeTheme}
              onThemeChange={setCodeTheme}
            />
          </div>

          {post.poll && (
            <PollWidget poll={post.poll} onUpdate={(updated) => setPost((p) => ({ ...p, poll: updated }))} />
          )}

          {post.nextPost && (
            <div className="next-post-recommendation">
              <div className="next-post-label">推薦接著看</div>
              <Link to={`/posts/${post.nextPost.id}`} className="next-post-link">
                <h4>{post.nextPost.title}</h4>
                <p className="next-post-author">作者：{post.nextPost.author_name}</p>
              </Link>
            </div>
          )}

          <div className="post-engagement">
            <button
              className={`btn-post-vote ${post.user_has_voted ? 'voted' : ''}`}
              onClick={handleVotePost}
              disabled={!user}
              title={user ? '對這篇文章按讚' : '登入後可按讚'}
            >
              👍 <span className="vote-num">{post.vote_count || 0}</span>
            </button>
            <button
              className={`btn-post-bookmark ${post.is_bookmarked ? 'bookmarked' : ''}`}
              onClick={handleToggleBookmarkPost}
              disabled={!user}
              title={user ? (post.is_bookmarked ? '取消收藏' : '收藏') : '登入後可收藏'}
            >
              {post.is_bookmarked ? '★ 已收藏' : '☆ 收藏'}
            </button>
          </div>

          <div className="post-tags">
            {post.tags?.map((tag) => (
              <span key={tag.id} className="tag">
                {tag.name}
                {user && (
                  <FollowButton
                    targetType="TAG"
                    targetId={tag.id}
                    subscribed={subscribedTags.has(tag.id)}
                    size="sm"
                    onChange={(next) => setSubscribedTags((prev) => {
                      const s = new Set(prev);
                      if (next) s.add(tag.id); else s.delete(tag.id);
                      return s;
                    })}
                  />
                )}
              </span>
            ))}
          </div>

          {(user?.id === post.author_id || user?.role === 'ADMIN') && (
            <div className="post-actions">
              {user?.id === post.author_id && (
                <Link to={`/posts/${id}/edit`} className="btn btn-secondary">
                  編輯
                </Link>
              )}
              {canResolvePost && (
                <button className="btn btn-primary" onClick={() => handleUpdatePostStatus('RESOLVED')}>
                  標記為已解決
                </button>
              )}
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (window.confirm('確定要刪除嗎？')) {
                    postService.delete(id).then(() => navigate('/'));
                  }
                }}
              >
                刪除
              </button>
            </div>
          )}
        </div>

        <div className="comments-section">
          <h2>留言 ({comments.length})</h2>

          {user && (
            <form onSubmit={handleAddComment} className="comment-form">
              <textarea
                className="form-control"
                placeholder="分享你的想法..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows="4"
                disabled={submitting}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || !newComment.trim()}
              >
                {submitting ? '提交中...' : '提交'}
              </button>
            </form>
          )}

          <div className="comments-list">
            {comments.map((comment) => (
              <CommentThread
                key={comment.id}
                comment={comment}
                post={post}
                user={user}
                onVote={handleVote}
                onAcceptAnswer={handleAcceptAnswer}
                onDelete={handleDeleteComment}
                replyingTo={replyingTo}
                setReplyingTo={setReplyingTo}
                replyContent={replyContent}
                onReplyContentChange={setReplyContent}
                onSubmitReply={handleAddReply}
                submitting={submitting}
              />
            ))}
          </div>
        </div>

        <RelatedPosts postId={id} />
      </div>
    </div>
  );
};

export default PostDetailPage;

